import { ChevronDown, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  id: number | string
  label: string
  color?: string
}

/**
 * Multi-select dropdown: zoznam voliteľných hodnôt, ktoré sa zbierajú do
 * chipov v hlavičke. Rovnaký dropdown-so-zoznamom-checkboxov ako
 * `ColumnPicker`, len ako všeobecná komponenta pre číselníky (jazyky,
 * kurzy...), nie len stĺpce tabuľky.
 *
 * Vyhľadávanie sa zobrazí až od `searchThreshold` položiek - pri krátkom
 * zozname (pár kurzov) by len prekážalo.
 */
/** Koľko štítkov sa zmestí do poľa, kým sa zvyšok zhrnie do „+N“. */
const MAX_CHIPS = 3

export default function MultiSelect({
  options,
  selected,
  onToggle,
  placeholder,
  disabled,
  className,
  searchThreshold = 8,
  onSelectAll,
  onClearAll,
}: {
  options: MultiSelectOption[]
  selected: ReadonlySet<number | string>
  onToggle: (id: number | string) => void
  placeholder: string
  disabled?: boolean
  className?: string
  searchThreshold?: number
  /** Označiť všetky **zobrazené** (pri vyhľadávaní len nájdené). */
  onSelectAll?: (ids: Array<number | string>) => void
  onClearAll?: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const box = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    search.current?.focus()
  }, [open])

  const showSearch = options.length >= searchThreshold
  const needle = query.trim().toLowerCase()
  const visible = needle
    ? options.filter((option) => option.label.toLowerCase().includes(needle))
    : options

  const chosen = options.filter((option) => selected.has(option.id))

  return (
    <div className={cn('relative', className)} ref={box}>
      <button
        type="button"
        className="input flex min-h-[38px] w-full flex-nowrap items-center gap-1.5 overflow-hidden text-left disabled:cursor-not-allowed disabled:opacity-60"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        disabled={disabled}
      >
        {/* Pole ostáva v jednom riadku: pri všetkých vybraných súhrn, pri
            viacerých prvé štítky a „+N“ - inak by sa po „Označiť všetky“
            roztiahlo na niekoľko riadkov. */}
        {chosen.length > 1 && chosen.length === options.length ? (
          <span className="truncate text-slate-700">
            {t('global.allSelected', { count: chosen.length })}
          </span>
        ) : chosen.length ? (
          [...chosen.slice(0, chosen.length > MAX_CHIPS ? MAX_CHIPS - 1 : MAX_CHIPS).map((option) => (
            <span
              key={option.id}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700"
            >
              {option.color ? (
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: option.color }}
                />
              ) : null}
              {option.label}
            </span>
          )),
          chosen.length > MAX_CHIPS ? (
            <span key="more" className="shrink-0 text-xs font-medium text-slate-500">
              +{chosen.length - (MAX_CHIPS - 1)}
            </span>
          ) : null]
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <ChevronDown size={14} className="ml-auto shrink-0 text-slate-400" />
      </button>

      {open ? (
        <div className="absolute left-0 z-30 mt-1 max-h-80 w-full min-w-[16rem] overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
          {showSearch ? (
            <div className="flex items-center gap-1.5 border-b border-slate-100 px-2.5 py-1.5">
              <Search size={14} className="shrink-0 text-slate-400" />
              <input
                ref={search}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('global.search')}
                className="w-full border-0 p-0 text-sm outline-none focus:ring-0"
              />
            </div>
          ) : null}

          {onSelectAll || onClearAll ? (
            <div className="flex gap-3 border-b border-slate-100 px-3 py-1.5 text-xs">
              {onSelectAll ? (
                <button
                  type="button"
                  className="text-action-700 hover:underline disabled:opacity-40"
                  disabled={disabled || !visible.length}
                  onClick={() => onSelectAll(visible.map((option) => option.id))}
                >
                  {t('global.selectAll')}
                </button>
              ) : null}
              {onClearAll ? (
                <button
                  type="button"
                  className="text-slate-500 hover:underline disabled:opacity-40"
                  disabled={disabled || !chosen.length}
                  onClick={onClearAll}
                >
                  {t('global.clearAll')}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="max-h-64 overflow-auto py-1">
            {visible.length ? (
              visible.map((option) => (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(option.id)}
                    disabled={disabled}
                    onChange={() => onToggle(option.id)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-action-600 focus:ring-action-400"
                  />
                  {option.color ? (
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  ) : null}
                  <span>{option.label}</span>
                </label>
              ))
            ) : (
              <p className="px-3 py-1.5 text-sm text-slate-400">{t('global.noResults')}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
